import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Archive,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  CopyPlus,
  Eye,
  EyeOff,
  FileText,
  FilterX,
  Languages,
  Pencil,
  Plus,
  RefreshCw,
  Rocket,
  Save,
  Search,
  X,
} from 'lucide-react';
import { api } from '../services/apiClient';
import './LegalDocuments.css';

const PAGE_SIZE = 20;

const DOCUMENT_TYPES = [
  { value: 'TOS', label: 'Termini di servizio' },
  { value: 'PRIVACY', label: 'Privacy policy' },
  { value: 'BETA_TERMS', label: 'Condizioni beta' },
  { value: 'DASHBOARD_CONFIGURATION_RESPONSIBILITY', label: 'Responsabilità configurazione dashboard' },
  { value: 'WORKER_STRIPE_TERMS', label: 'Condizioni Stripe lavoratore' },
  { value: 'WORKER_PAYMENT_TERMS', label: 'Condizioni pagamenti lavoratore' },
  { value: 'KIOSK_DISCLAIMER', label: 'Disclaimer kiosk' },
  { value: 'CONTRACT_TEMPLATE_ONLY', label: 'Contratto principale' },
  { value: 'ANNEX_TEMPLATE', label: 'Allegato A economico' },
  { value: 'CONTRACT_TEMPLATE', label: 'Contratto legacy' },
  { value: 'ADMIN_INTERNAL_AUDIT', label: 'Audit interno' },
];

const LOCALES = [
  { value: 'it', label: 'Italiano' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
];

const STATUS_LABELS = {
  DRAFT: 'Bozza',
  PUBLISHED: 'Pubblicato',
  ARCHIVED: 'Archiviato',
};

const emptyForm = {
  type: 'TOS',
  version: '',
  title: '',
  subtitle: '',
  html: '',
  locale: 'it',
  status: 'DRAFT',
};

const escapeHtml = (value) => String(value || '').replace(/[&<>"']/g, (char) => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}[char]));

const renderLegalDocumentPreview = (html, { version, publishedAt, locale }) => {
  const effectiveDate = publishedAt
    ? new Intl.DateTimeFormat(locale || 'it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'Europe/Rome',
      }).format(new Date(publishedAt))
    : '[assegnata alla pubblicazione]';

  return String(html || '')
    .replace(/\{\{documentVersion\}\}/g, escapeHtml(version))
    .replace(/\{\{documentEffectiveDate\}\}/g, effectiveDate);
};

const getDocumentTypeConfig = (type) => (
  DOCUMENT_TYPES.find((item) => item.value === type) || null
);

const getDocumentTypeLabel = (type, fallbackTitle = '') => (
  getDocumentTypeConfig(type)?.label || fallbackTitle || type
);

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('it-IT', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const LegalDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    totalPages: 1,
  });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    locale: '',
    search: '',
  });
  const [showArchived, setShowArchived] = useState(false);

  const [editingDoc, setEditingDoc] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [localeVariantMode, setLocaleVariantMode] = useState(false);
  const [editForm, setEditForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [publishingId, setPublishingId] = useState(null);

  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showContentModal, setShowContentModal] = useState(false);

  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const queryParams = new URLSearchParams({
        page: String(page),
        limit: String(PAGE_SIZE),
      });
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
      if (showArchived || filters.status === 'ARCHIVED') {
        queryParams.append('includeArchived', 'true');
      }

      const response = await api.get(`/api/admin/legal-documents?${queryParams.toString()}`);
      setDocuments(response.data || []);
      setPagination(response.pagination || {
        total: response.data?.length || 0,
        page,
        totalPages: 1,
      });
    } catch (err) {
      console.error('Error loading legal documents:', err);
      setError(err.response?.data?.error || 'Impossibile caricare i documenti legali.');
    } finally {
      setLoading(false);
    }
  }, [filters, page, showArchived]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const pageStats = useMemo(() => documents.reduce((stats, document) => {
    stats[document.status] = (stats[document.status] || 0) + 1;
    return stats;
  }, { PUBLISHED: 0, DRAFT: 0, ARCHIVED: 0 }), [documents]);

  const availableTypes = useMemo(() => {
    const knownValues = new Set(DOCUMENT_TYPES.map((item) => item.value));
    const unknownTypes = documents
      .map((document) => document.type)
      .filter((type) => !knownValues.has(type))
      .map((type) => ({ value: type, label: type }));
    return [...DOCUMENT_TYPES, ...unknownTypes];
  }, [documents]);

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
    if (key === 'status' && value === 'ARCHIVED') {
      setShowArchived(true);
    }
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({ type: '', status: '', locale: '', search: '' });
    setShowArchived(false);
    setPage(1);
  };

  const openCreateModal = () => {
    setEditingDoc(null);
    setLocaleVariantMode(false);
    setEditForm(emptyForm);
    setShowEditor(true);
  };

  const openEditModal = (document) => {
    setEditingDoc(document);
    setLocaleVariantMode(false);
    setEditForm({
      type: document.type,
      version: document.version,
      title: document.title || '',
      subtitle: document.subtitle || '',
      html: document.html || '',
      locale: document.locale,
      status: document.status,
    });
    setShowEditor(true);
  };

  const closeEditor = () => {
    if (saving) return;
    setShowEditor(false);
    setEditingDoc(null);
    setLocaleVariantMode(false);
    setEditForm(emptyForm);
  };

  const openContentModal = (document) => {
    setSelectedDoc(document);
    setShowContentModal(true);
  };

  const closeContentModal = () => {
    setShowContentModal(false);
    setSelectedDoc(null);
  };

  const handleEditFormChange = (event) => {
    const { name, value } = event.target;
    setEditForm((current) => ({ ...current, [name]: value }));
  };

  const startLocaleVariant = () => {
    setLocaleVariantMode(true);
    const alternativeLocale = LOCALES.find((locale) => locale.value !== editingDoc.locale)?.value || 'en';
    setEditForm((current) => ({
      ...current,
      locale: alternativeLocale,
      status: 'DRAFT',
    }));
  };

  const cancelLocaleVariant = () => {
    setLocaleVariantMode(false);
    setEditForm((current) => ({
      ...current,
      locale: editingDoc.locale,
      status: editingDoc.status,
    }));
  };

  const validateForm = () => {
    if (!editForm.type || !editForm.version.trim() || !editForm.html.trim()) {
      alert('Tipo, versione e contenuto HTML sono obbligatori.');
      return false;
    }
    if (editingDoc && editingDoc.status !== 'DRAFT' && editingDoc.version === editForm.version && !localeVariantMode) {
      alert('I documenti pubblicati o archiviati sono immutabili. Cambia versione per creare una nuova bozza.');
      return false;
    }
    if (localeVariantMode && editForm.locale === editingDoc.locale) {
      alert('Seleziona una lingua diversa per creare la variante.');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);

      if (!editingDoc) {
        await api.post('/api/admin/legal-documents', {
          ...editForm,
          status: 'DRAFT',
        });
        alert('Documento creato come bozza.');
      } else if (localeVariantMode) {
        await api.post('/api/admin/legal-documents', {
          type: editingDoc.type,
          version: editForm.version,
          title: editForm.title,
          subtitle: editForm.subtitle,
          html: editForm.html,
          locale: editForm.locale,
          status: 'DRAFT',
        });
        alert('Variante linguistica creata come bozza.');
      } else {
        const response = await api.put(`/api/admin/legal-documents/${editingDoc.id}`, editForm);
        alert(response.isNewVersion
          ? 'Nuova versione creata come bozza.'
          : 'Bozza aggiornata correttamente.');
      }

      closeEditor();
      await loadDocuments();
    } catch (err) {
      console.error('Error saving legal document:', err);
      alert(err.response?.data?.error || 'Impossibile salvare il documento.');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (document) => {
    if (!window.confirm(
      `Pubblicare “${getDocumentTypeLabel(document.type)}” v${document.version}?\n\n`
      + `La versione attualmente pubblicata in ${document.locale.toUpperCase()} verrà archiviata.`
    )) {
      return;
    }

    try {
      setPublishingId(document.id);
      await api.post(`/api/admin/legal-documents/${document.id}/publish`);
      await loadDocuments();
    } catch (err) {
      console.error('Error publishing legal document:', err);
      alert(err.response?.data?.error || 'Impossibile pubblicare il documento.');
    } finally {
      setPublishingId(null);
    }
  };

  const handleArchive = async (document) => {
    if (!window.confirm(
      `Archiviare “${getDocumentTypeLabel(document.type)}” v${document.version}?`
    )) {
      return;
    }

    try {
      await api.delete(`/api/admin/legal-documents/${document.id}`);
      await loadDocuments();
    } catch (err) {
      console.error('Error archiving legal document:', err);
      alert(err.response?.data?.error || 'Impossibile archiviare il documento.');
    }
  };

  const previewPublishedAt = editingDoc
    && editForm.version === editingDoc.version
    && !localeVariantMode
    ? editingDoc.publishedAt
    : null;
  const previewHtml = renderLegalDocumentPreview(editForm.html, {
    version: editForm.version,
    publishedAt: previewPublishedAt,
    locale: editForm.locale,
  });
  const hasActiveFilters = Object.values(filters).some(Boolean) || showArchived;

  return (
    <div className="ld-page">
      <header className="ld-page-header">
        <div>
          <div className="ld-eyebrow">Compliance e versionamento</div>
          <h1>Documenti legali</h1>
          <p>Gestisci bozze, pubblicazioni, lingue e date di efficacia da un unico punto.</p>
        </div>
        <div className="ld-header-actions">
          <button
            type="button"
            className="ld-button ld-button--secondary"
            onClick={loadDocuments}
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'ld-spin' : ''} />
            Aggiorna
          </button>
          <button type="button" className="ld-button ld-button--primary" onClick={openCreateModal}>
            <Plus size={17} />
            Nuovo documento
          </button>
        </div>
      </header>

      <section className="ld-kpi-grid" aria-label="Riepilogo documenti nella pagina corrente">
        <div className="ld-kpi-card ld-kpi-card--total">
          <div className="ld-kpi-icon"><FileText size={19} /></div>
          <div>
            <span>Risultati</span>
            <strong>{pagination.total}</strong>
          </div>
        </div>
        <div className="ld-kpi-card ld-kpi-card--published">
          <div className="ld-kpi-icon"><CheckCircle2 size={19} /></div>
          <div>
            <span>Pubblicati in pagina</span>
            <strong>{pageStats.PUBLISHED}</strong>
          </div>
        </div>
        <div className="ld-kpi-card ld-kpi-card--draft">
          <div className="ld-kpi-icon"><Clock3 size={19} /></div>
          <div>
            <span>Bozze in pagina</span>
            <strong>{pageStats.DRAFT}</strong>
          </div>
        </div>
        <div className="ld-kpi-card ld-kpi-card--archived">
          <div className="ld-kpi-icon"><Archive size={19} /></div>
          <div>
            <span>Archiviati in pagina</span>
            <strong>{pageStats.ARCHIVED}</strong>
          </div>
        </div>
      </section>

      <section className="ld-filter-card">
        <div className="ld-filter-heading">
          <div>
            <strong>Ricerca e filtri</strong>
            <span>Trova rapidamente una versione o limita la vista per stato e lingua.</span>
          </div>
          <div className="ld-filter-heading-actions">
            <label className="ld-archive-toggle">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setShowArchived(checked);
                  if (!checked && filters.status === 'ARCHIVED') {
                    setFilters((current) => ({ ...current, status: '' }));
                  }
                  setPage(1);
                }}
              />
              <span className="ld-toggle-track" aria-hidden="true">
                <span />
              </span>
              {showArchived ? <Eye size={15} /> : <EyeOff size={15} />}
              Mostra archivio
            </label>
            {hasActiveFilters && (
              <button type="button" className="ld-clear-filters" onClick={resetFilters}>
                <FilterX size={15} />
                Azzera filtri
              </button>
            )}
          </div>
        </div>
        <div className="ld-filter-grid">
          <label className="ld-field ld-field--search">
            <span>Cerca</span>
            <div className="ld-input-icon">
              <Search size={16} />
              <input
                type="search"
                placeholder="Tipo, versione, titolo o contenuto…"
                value={filters.search}
                onChange={(event) => updateFilter('search', event.target.value)}
              />
            </div>
          </label>
          <label className="ld-field">
            <span>Tipo documento</span>
            <select value={filters.type} onChange={(event) => updateFilter('type', event.target.value)}>
              <option value="">Tutti i tipi</option>
              {availableTypes.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </label>
          <label className="ld-field">
            <span>Stato</span>
            <select value={filters.status} onChange={(event) => updateFilter('status', event.target.value)}>
              <option value="">Tutti gli stati</option>
              <option value="PUBLISHED">Pubblicato</option>
              <option value="DRAFT">Bozza</option>
              <option value="ARCHIVED">Archiviato</option>
            </select>
          </label>
          <label className="ld-field">
            <span>Lingua</span>
            <select value={filters.locale} onChange={(event) => updateFilter('locale', event.target.value)}>
              <option value="">Tutte le lingue</option>
              {LOCALES.map((locale) => (
                <option key={locale.value} value={locale.value}>{locale.label}</option>
              ))}
            </select>
          </label>
        </div>
      </section>

      {error && (
        <div className="ld-alert ld-alert--error" role="alert">
          <strong>Caricamento non riuscito</strong>
          <span>{error}</span>
          <button type="button" onClick={loadDocuments}>Riprova</button>
        </div>
      )}

      <section className="ld-table-card" aria-busy={loading}>
        <div className="ld-table-heading">
          <div>
            <strong>Versioni documenti</strong>
            <span>
              {pagination.total === 0
                ? 'Nessun risultato'
                : `${pagination.total} ${pagination.total === 1 ? 'documento' : 'documenti'}`}
            </span>
          </div>
          <div className="ld-workflow-legend">
            <span><i className="ld-dot ld-dot--draft" /> Bozza modificabile</span>
            <span><i className="ld-dot ld-dot--published" /> Versione effettiva</span>
          </div>
        </div>

        <div className="ld-table-scroll">
          <table className="ld-table">
            <thead>
              <tr>
                <th>Documento</th>
                <th>Versione</th>
                <th>Lingua</th>
                <th>Stato</th>
                <th>Efficace dal</th>
                <th>Creato il</th>
                <th><span className="ld-sr-only">Azioni</span></th>
              </tr>
            </thead>
            <tbody>
              {documents.map((document) => (
                <tr key={document.id} className={document.status === 'ARCHIVED' ? 'ld-row--archived' : ''}>
                  <td>
                    <button
                      type="button"
                      className="ld-document-cell"
                      onClick={() => openContentModal(document)}
                    >
                      <span className="ld-document-icon"><FileText size={17} /></span>
                      <span>
                        <strong>{getDocumentTypeLabel(document.type, document.title)}</strong>
                        <small>{document.type}</small>
                        {getDocumentTypeConfig(document.type) && document.title && (
                          <em><b>Titolo:</b> {document.title}</em>
                        )}
                        {document.subtitle && (
                          <em><b>Sottotitolo:</b> {document.subtitle}</em>
                        )}
                      </span>
                    </button>
                  </td>
                  <td><span className="ld-version">v{document.version}</span></td>
                  <td><span className="ld-locale">{document.locale.toUpperCase()}</span></td>
                  <td>
                    <span className={`ld-status ld-status--${document.status.toLowerCase()}`}>
                      {STATUS_LABELS[document.status] || document.status}
                    </span>
                  </td>
                  <td className="ld-date-cell">
                    {document.publishedAt ? (
                      <>
                        <strong>{formatDate(document.publishedAt)}</strong>
                        <small>Data di entrata in vigore</small>
                      </>
                    ) : (
                      <span className="ld-muted">Non ancora effettivo</span>
                    )}
                  </td>
                  <td className="ld-date-cell">
                    <span>{formatDate(document.createdAt)}</span>
                  </td>
                  <td>
                    <div className="ld-row-actions">
                      <button
                        type="button"
                        className="ld-icon-button"
                        onClick={() => openContentModal(document)}
                        title="Visualizza documento"
                        aria-label={`Visualizza ${document.type} versione ${document.version}`}
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        type="button"
                        className="ld-icon-button"
                        onClick={() => openEditModal(document)}
                        title={document.status === 'DRAFT' ? 'Modifica bozza' : 'Crea nuova versione'}
                        aria-label={document.status === 'DRAFT' ? 'Modifica bozza' : 'Crea nuova versione'}
                      >
                        {document.status === 'DRAFT' ? <Pencil size={16} /> : <CopyPlus size={16} />}
                      </button>
                      {document.status === 'DRAFT' && (
                        <button
                          type="button"
                          className="ld-icon-button ld-icon-button--publish"
                          onClick={() => handlePublish(document)}
                          title="Pubblica questa versione"
                          aria-label="Pubblica questa versione"
                          disabled={publishingId === document.id}
                        >
                          <Rocket size={16} />
                        </button>
                      )}
                      {document.status !== 'ARCHIVED' && (
                        <button
                          type="button"
                          className="ld-icon-button ld-icon-button--danger"
                          onClick={() => handleArchive(document)}
                          title="Archivia"
                          aria-label="Archivia documento"
                        >
                          <Archive size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!loading && documents.length === 0 && (
          <div className="ld-empty-state">
            <div><FileText size={26} /></div>
            <strong>Nessun documento trovato</strong>
            <p>Modifica i filtri oppure crea un nuovo documento legale.</p>
            {hasActiveFilters ? (
              <button type="button" className="ld-button ld-button--secondary" onClick={resetFilters}>
                Azzera filtri
              </button>
            ) : (
              <button type="button" className="ld-button ld-button--primary" onClick={openCreateModal}>
                <Plus size={16} /> Nuovo documento
              </button>
            )}
          </div>
        )}

        {loading && (
          <div className="ld-loading-overlay">
            <RefreshCw size={20} className="ld-spin" />
            Aggiornamento documenti…
          </div>
        )}

        {pagination.totalPages > 1 && (
          <footer className="ld-pagination">
            <span>Pagina {pagination.page} di {pagination.totalPages}</span>
            <div>
              <button
                type="button"
                className="ld-icon-button"
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={pagination.page <= 1 || loading}
                aria-label="Pagina precedente"
              >
                <ChevronLeft size={17} />
              </button>
              <button
                type="button"
                className="ld-icon-button"
                onClick={() => setPage((current) => Math.min(pagination.totalPages, current + 1))}
                disabled={pagination.page >= pagination.totalPages || loading}
                aria-label="Pagina successiva"
              >
                <ChevronRight size={17} />
              </button>
            </div>
          </footer>
        )}
      </section>

      {showEditor && (
        <div className="ld-modal-overlay" onMouseDown={closeEditor}>
          <div className="ld-editor-modal" onMouseDown={(event) => event.stopPropagation()}>
            <header className="ld-modal-header">
              <div>
                <span className="ld-modal-icon">
                  {localeVariantMode ? <Languages size={18} /> : editingDoc ? <Pencil size={18} /> : <Plus size={18} />}
                </span>
                <div>
                  <h2>
                    {!editingDoc
                      ? 'Nuovo documento'
                      : localeVariantMode
                        ? 'Crea variante linguistica'
                        : editingDoc.status === 'DRAFT'
                          ? 'Modifica bozza'
                          : 'Crea nuova versione'}
                  </h2>
                  <p>
                    {editingDoc
                      ? `${getDocumentTypeLabel(editingDoc.type)} · ${editingDoc.locale.toUpperCase()} · v${editingDoc.version}`
                      : 'Il documento verrà creato in stato bozza.'}
                  </p>
                </div>
              </div>
              <button type="button" className="ld-modal-close" onClick={closeEditor} aria-label="Chiudi">
                <X size={20} />
              </button>
            </header>

            <div className="ld-editor-body">
              <section className="ld-editor-form">
                {editingDoc && editingDoc.status !== 'DRAFT' && !localeVariantMode && (
                  <div className="ld-alert ld-alert--info">
                    <CopyPlus size={18} />
                    <div>
                      <strong>Versione immutabile</strong>
                      <span>Cambia il numero di versione: il salvataggio creerà una nuova bozza senza alterare il documento corrente.</span>
                    </div>
                  </div>
                )}

                {localeVariantMode && (
                  <div className="ld-alert ld-alert--info">
                    <Languages size={18} />
                    <div>
                      <strong>Nuova variante linguistica</strong>
                      <span>Traduci il contenuto e scegli la lingua. La variante verrà creata come bozza.</span>
                    </div>
                    <button type="button" onClick={cancelLocaleVariant}>Annulla</button>
                  </div>
                )}

                <div className="ld-form-grid">
                  <label className="ld-field ld-field--wide">
                    <span>Tipo documento *</span>
                    <select
                      name="type"
                      value={editForm.type}
                      onChange={handleEditFormChange}
                      disabled={Boolean(editingDoc)}
                    >
                      {DOCUMENT_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="ld-field">
                    <span>Versione *</span>
                    <input
                      name="version"
                      value={editForm.version}
                      onChange={handleEditFormChange}
                      placeholder="es. 1.0"
                    />
                  </label>
                  <label className="ld-field">
                    <span>Lingua *</span>
                    <select
                      name="locale"
                      value={editForm.locale}
                      onChange={handleEditFormChange}
                      disabled={Boolean(editingDoc) && !localeVariantMode}
                    >
                      {LOCALES.map((locale) => (
                        <option key={locale.value} value={locale.value}>{locale.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="ld-field ld-field--wide">
                    <span>Titolo visualizzato</span>
                    <input
                      name="title"
                      value={editForm.title}
                      onChange={handleEditFormChange}
                      placeholder="Titolo mostrato all’utente"
                    />
                  </label>
                  <label className="ld-field ld-field--wide">
                    <span>Testo di consenso / sottotitolo</span>
                    <input
                      name="subtitle"
                      value={editForm.subtitle}
                      onChange={handleEditFormChange}
                      placeholder="Descrizione breve mostrata nel flusso di consenso"
                    />
                  </label>
                </div>

                <div className="ld-html-field">
                  <div className="ld-html-heading">
                    <div>
                      <label htmlFor="legal-document-html">Contenuto HTML *</label>
                      <span>{editForm.html.length.toLocaleString('it-IT')} caratteri</span>
                    </div>
                    </div>
                    <textarea
                      id="legal-document-html"
                    name="html"
                    value={editForm.html}
                    onChange={handleEditFormChange}
                    spellCheck="false"
                    placeholder="<h1>Titolo del documento</h1>"
                  />
                </div>
              </section>

              <aside className="ld-editor-preview">
                <div className="ld-preview-heading">
                  <div>
                    <span>Anteprima documento</span>
                    <small>
                      v{editForm.version || '—'} · {editForm.locale.toUpperCase()}
                    </small>
                  </div>
                  <span className="ld-status ld-status--draft">Bozza</span>
                </div>
                <div className="ld-preview-meta">
                  <strong>{editForm.title || getDocumentTypeLabel(editForm.type)}</strong>
                  {editForm.subtitle && <span>{editForm.subtitle}</span>}
                </div>
                <iframe
                  className="ld-preview-frame"
                  title="Anteprima documento legale"
                  srcDoc={previewHtml || '<p style="color:#64748b;font-family:system-ui">Inserisci il contenuto HTML per visualizzare l’anteprima.</p>'}
                  sandbox=""
                />
              </aside>
            </div>

            <footer className="ld-modal-footer">
              <div>
                {editingDoc && !localeVariantMode && (
                  <button type="button" className="ld-button ld-button--ghost" onClick={startLocaleVariant}>
                    <Languages size={16} />
                    Crea variante lingua
                  </button>
                )}
              </div>
              <div>
                <button type="button" className="ld-button ld-button--secondary" onClick={closeEditor}>
                  Annulla
                </button>
                <button
                  type="button"
                  className="ld-button ld-button--primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  <Save size={16} />
                  {saving
                    ? 'Salvataggio…'
                    : !editingDoc
                      ? 'Crea bozza'
                      : localeVariantMode
                        ? 'Crea variante'
                        : editingDoc.status === 'DRAFT'
                          ? 'Salva bozza'
                          : 'Crea nuova bozza'}
                </button>
              </div>
            </footer>
          </div>
        </div>
      )}

      {showContentModal && selectedDoc && (
        <div className="ld-modal-overlay" onMouseDown={closeContentModal}>
          <div className="ld-view-modal" onMouseDown={(event) => event.stopPropagation()}>
            <header className="ld-modal-header">
              <div>
                <span className="ld-modal-icon"><FileText size={18} /></span>
                <div>
                  <h2>{getDocumentTypeLabel(selectedDoc.type, selectedDoc.title)}</h2>
                  <p>{selectedDoc.type} · v{selectedDoc.version} · {selectedDoc.locale.toUpperCase()}</p>
                </div>
              </div>
              <button type="button" className="ld-modal-close" onClick={closeContentModal} aria-label="Chiudi">
                <X size={20} />
              </button>
            </header>

            <div className="ld-view-meta">
              <span className={`ld-status ld-status--${selectedDoc.status.toLowerCase()}`}>
                {STATUS_LABELS[selectedDoc.status]}
              </span>
              <div>
                <small>Creato il</small>
                <strong>{formatDate(selectedDoc.createdAt)}</strong>
              </div>
              <div>
                <small>Efficace dal</small>
                <strong>{formatDate(selectedDoc.publishedAt)}</strong>
              </div>
              {selectedDoc.subtitle && <p>{selectedDoc.subtitle}</p>}
            </div>

            <iframe
              className="ld-document-frame"
              title={`${selectedDoc.type}-v${selectedDoc.version}`}
              srcDoc={renderLegalDocumentPreview(selectedDoc.html, {
                version: selectedDoc.version,
                publishedAt: selectedDoc.publishedAt,
                locale: selectedDoc.locale,
              })}
              sandbox=""
            />

            <footer className="ld-modal-footer">
              <div />
              <div>
                <button type="button" className="ld-button ld-button--secondary" onClick={closeContentModal}>
                  Chiudi
                </button>
                <button
                  type="button"
                  className="ld-button ld-button--primary"
                  onClick={() => {
                    closeContentModal();
                    openEditModal(selectedDoc);
                  }}
                >
                  {selectedDoc.status === 'DRAFT' ? <Pencil size={16} /> : <CopyPlus size={16} />}
                  {selectedDoc.status === 'DRAFT' ? 'Modifica bozza' : 'Crea nuova versione'}
                </button>
              </div>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

export default LegalDocuments;
